/**
 * PHASE 8: Mobile Week Focus Strip
 * Shows the selected week (7 days) as tappable pills/cells
 * Appears below command bar when a day is selected
 */

import React from 'react';
import { formatDateOnlyET } from '../../utils/dateTimeET.js';

export default function MobileWeekStrip({ 
  weekStart, 
  selectedDate, 
  bookings = [], 
  onDayClick,
  isMobile = false 
}) {
  if (!isMobile || !weekStart || !selectedDate) {
    return null;
  }

  // Generate 7 days for the week (Sunday to Saturday)
  const weekDays = [];
  for (let i = 0; i < 7; i++) {
    const day = new Date(weekStart);
    day.setDate(weekStart.getDate() + i);
    weekDays.push(day);
  }

  // Helper to check if a day has bookings
  const hasBookings = (date) => {
    if (!bookings || bookings.length === 0) return false;
    const dateStr = date.toISOString().split('T')[0];
    return bookings.some(booking => {
      const checkIn = booking.check_in_date ? new Date(booking.check_in_date).toISOString().split('T')[0] : null;
      const checkOut = booking.check_out_date ? new Date(booking.check_out_date).toISOString().split('T')[0] : null;
      if (!checkIn || !checkOut) return false;
      return dateStr >= checkIn && dateStr < checkOut;
    });
  };

  // Helper to check if a date is selected
  const isSelected = (date) => {
    if (!selectedDate) return false;
    const dateStr = date.toISOString().split('T')[0];
    const selectedStr = selectedDate instanceof Date 
      ? selectedDate.toISOString().split('T')[0]
      : new Date(selectedDate).toISOString().split('T')[0];
    return dateStr === selectedStr;
  };

  const dayNames = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

  return (
    <div style={styles.container}>
      <div style={styles.weekStrip}>
        {weekDays.map((day, index) => {
          const dayHasBookings = hasBookings(day);
          const dayIsSelected = isSelected(day);
          
          return (
            <button
              key={index}
              onClick={() => onDayClick && onDayClick(day)}
              style={{
                ...styles.dayPill,
                ...(dayIsSelected ? styles.dayPillSelected : {}),
              }}
              aria-label={`${dayNames[index]} ${formatDateOnlyET(day.toISOString().split('T')[0], { monthDay: true })}`}
              aria-current={dayIsSelected ? 'true' : 'false'}
            >
              <div style={styles.dayName}>{dayNames[index]}</div>
              <div style={styles.dayNumber}>{day.getDate()}</div>
              {dayHasBookings && (
                <div style={styles.bookingIndicator} aria-label="Has bookings" />
              )}
            </button>
          );
        })}
      </div>
    </div>
  );
}

const styles = {
  // PHASE 9D: Week strip - Spatial continuity (smooth appear transition)
  container: {
    position: 'sticky',
    top: 'calc(var(--owner-top-nav-height, 52px) + 48px)', // PHASE 9C: Below command bar (48px height)
    zIndex: 90, // Below command bar (100) but above content
    backgroundColor: '#ffffff',
    borderBottom: '1px solid var(--color-border-light)', // PHASE 9C: Hairline divider only (no heavy border)
    borderTop: 'none', // PHASE 9C: No top border (surface, not card)
    boxShadow: 'none', // PHASE 9C: No shadow (surface, not floating panel)
    // PHASE 9C: Minimal padding (8px vertical) - surface, not card
    padding: 'var(--spacing-2) var(--spacing-4)', // PHASE 9C: 8px vertical padding
    // PHASE 9D: Smooth appear transition (spatial continuity)
    animation: 'fadeInSlideDown 150ms ease-out', // PHASE 9D: Smooth appear
  },
  weekStrip: {
    display: 'flex',
    gap: 'var(--spacing-1)',
    justifyContent: 'space-between',
    alignItems: 'stretch',
    // PHASE 9A: Edge-to-edge - remove centered container (maxWidth + margin auto)
    width: '100%',
  },
  // PHASE 9D: Day pill - Touch feedback and smooth transitions
  dayPill: {
    flex: '1 1 0',
    minWidth: 0,
    padding: 'var(--spacing-2) var(--spacing-1)',
    backgroundColor: 'var(--color-bg-primary)',
    border: '1px solid var(--color-border-light)',
    borderRadius: 'var(--radius-md)',
    cursor: 'pointer',
    minHeight: '44px', // PHASE 8: Touch target
    display: 'flex',
    flexDirection: 'column',
    alignItems: 'center',
    justifyContent: 'center',
    gap: 'var(--spacing-1)',
    // PHASE 9D: Fast, subtle transitions (80ms for touch feedback)
    transition: 'background-color 80ms ease-out, transform 80ms ease-out, border-color 120ms ease-out',
    position: 'relative',
    WebkitTapHighlightColor: 'transparent', // PHASE 9D: Remove default tap highlight
  },
  dayPillSelected: {
    backgroundColor: 'var(--color-brand-primary, #2563eb)',
    borderColor: 'var(--color-brand-primary, #2563eb)',
    color: '#ffffff',
  },
  dayName: {
    fontSize: 'var(--font-size-xs)',
    fontWeight: 'var(--font-weight-medium)',
    textTransform: 'uppercase',
    letterSpacing: '0.05em',
    lineHeight: 1,
  },
  dayNumber: {
    fontSize: 'var(--font-size-base)',
    fontWeight: 'var(--font-weight-semibold)',
    lineHeight: 1,
  },
  bookingIndicator: {
    position: 'absolute',
    bottom: '4px',
    width: '4px',
    height: '4px',
    borderRadius: '50%',
    backgroundColor: 'var(--color-status-success-base, #10b981)',
  },
};
